import {
  useHttpClient,
  useNetworkStatus,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { type ProposalEngagement } from './proposalEngagement.models';
import {
  initState,
  proposalEngagementReducer,
  type ProposalEngagementState,
} from './proposalEngagement.reducer';

interface ProposalEngagementRequest {
  appointmentId: string;
  appointmentProposalId: string;
}

interface ProposalEngagementContextProps {
  proposalEngagements: ProposalEngagement[];
  loadingProposalIds: string[];
  fetchProposalEngagements: (requests: ProposalEngagementRequest[]) => void;
}

const ProposalEngagementContext = createContext<
  ProposalEngagementContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<ProposalEngagementState>;
}>;

export const ProposalEngagementProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(proposalEngagementReducer, {
    ...initState,
    ...initialState,
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();

  const proposalEngagements = useMemo(
    () => state.ids.map((id) => state.entities[id]),
    [state.ids, state.entities]
  );

  const fetchProposalEngagements: ProposalEngagementContextProps['fetchProposalEngagements'] =
    useCallback(
      async (requests) => {
        if (!requests.length || !isOnline()) {
          return;
        }

        const proposalIds = requests.map((r) => r.appointmentProposalId);
        dispatch({
          type: 'FETCH_PROPOSAL_ENGAGEMENTS',
          payload: { proposalIds },
        });
        try {
          const engagements = await httpClient.post<ProposalEngagement[]>(
            '/proposal-analytics/appointments/proposals/engagement',
            requests
          );
          dispatch({
            type: 'FETCH_PROPOSAL_ENGAGEMENTS_SUCCESS',
            payload: { proposalIds, engagements },
          });
        } catch {
          dispatch({
            type: 'FETCH_PROPOSAL_ENGAGEMENTS_FAILURE',
            payload: { proposalIds },
          });
        }
      },
      [httpClient, isOnline]
    );

  return (
    <ProposalEngagementContext.Provider
      value={{
        proposalEngagements,
        loadingProposalIds: state.loadingProposalIds,
        fetchProposalEngagements,
      }}
    >
      {children}
    </ProposalEngagementContext.Provider>
  );
};

export const useProposalEngagement = () => {
  const context = useContext(ProposalEngagementContext);
  if (context === undefined) {
    throw new Error(
      'useProposalEngagement must be used within a ProposalEngagementProvider'
    );
  }
  return context;
};
